// The one-glance summary of a logged bite: the date, how long to watch, and what to watch
// for. Shown beside the bite flow on wide screens and printed as the clinician's copy.
//
// Nothing here is new information. "How long" is the longest published onset window in
// ./onset for the illnesses in play, and "what to watch for" groups the `watchFor` text
// already written there. A sign group only appears when at least one of those illnesses
// lists it, so the summary can never warn about something its own sources do not say.

import { ONSET, addDays, daysBetween, longestWindowDays, type OnsetEntry } from "./onset";

export interface SignGroup {
  /** What the reader sees. Plain words, a phrase rather than a sentence. */
  label: string;
  /** Matched against each illness's `watchFor`. */
  match: RegExp;
}

/**
 * The signs, in the order a reader should check them. Alpha-gal is last and kept apart
 * because it runs on a different clock (hours after a meal, not days after the bite).
 */
export const SIGN_GROUPS: readonly SignGroup[] = [
  { label: "Fever or chills", match: /\bfever\b|\bchills\b/i },
  { label: "A rash, or a sore where the tick was", match: /\brash\b|\bulcer\b/i },
  {
    label: "A bad headache, body aches or feeling very tired",
    match: /headache|aches|fatigue|tiredness/i,
  },
  { label: "Swollen glands", match: /swollen glands/i },
  { label: "Vomiting, weakness or confusion", match: /vomiting|weakness|confusion/i },
];

export const ALPHA_GAL_SLUG = "alpha-gal-syndrome";

export interface BiteSummary {
  /** Day 0 is the day of the bite. Undefined until a bite is logged. */
  dayNumber?: number;
  /** The longest published window for these illnesses, in days from the bite. */
  watchDays?: number;
  /** Bite date plus watchDays. */
  watchUntil?: Date;
  /** Days from today to watchUntil, never below zero. */
  daysLeft?: number;
  /** True when one of the illnesses can show up after its published window. */
  openEnded: boolean;
  /** Illnesses whose published window is open today. */
  openNow: string[];
  /** Sign groups that at least one illness in play lists. */
  signs: string[];
  /** Whether alpha-gal is in play, so the summary can add its separate note. */
  alphaGal: boolean;
}

function inPlay(slugs?: readonly string[]): OnsetEntry[] {
  const allow = slugs && slugs.length > 0 ? new Set(slugs) : undefined;
  return ONSET.filter((e) => !allow || allow.has(e.slug));
}

/** The sign groups that the given illnesses' own watch-for text supports. */
export function signsFor(slugs?: readonly string[]): string[] {
  const entries = inPlay(slugs).filter((e) => e.slug !== ALPHA_GAL_SLUG);
  return SIGN_GROUPS.filter((g) => entries.some((e) => g.match.test(e.watchFor))).map(
    (g) => g.label,
  );
}

/**
 * Summarise one bite. With no bite date it still answers "how long" and "what for",
 * which is what someone who has not logged anything yet needs to know.
 */
export function summarizeBite({
  bitten,
  today,
  slugs,
}: {
  bitten?: Date;
  today: Date;
  slugs?: readonly string[];
}): BiteSummary {
  const entries = inPlay(slugs);
  const narrowed = slugs && slugs.length > 0 ? slugs : undefined;
  const watchDays = longestWindowDays(narrowed);
  const dayNumber = bitten ? daysBetween(bitten, today) : undefined;
  const watchUntil = bitten && watchDays !== undefined ? addDays(bitten, watchDays) : undefined;
  const daysLeft =
    watchUntil !== undefined ? Math.max(0, daysBetween(today, watchUntil)) : undefined;

  const openNow =
    dayNumber === undefined
      ? []
      : entries
          .filter((e) => {
            const w = e.window;
            if (!w) return false;
            const started = w.minDays === undefined || dayNumber >= w.minDays;
            const ended = w.maxDays !== undefined && dayNumber > w.maxDays;
            return started && !ended;
          })
          .map((e) => e.shortName);

  return {
    dayNumber,
    watchDays,
    watchUntil,
    daysLeft,
    openEnded: entries.some(
      (e) => e.window?.openEnded || (e.window === undefined && e.slug !== ALPHA_GAL_SLUG),
    ),
    openNow,
    signs: signsFor(narrowed),
    alphaGal: entries.some((e) => e.slug === ALPHA_GAL_SLUG),
  };
}
